import Alert from '@pelckmans/elementis/components/alert';
import Blockheader from '@pelckmans/elementis/components/blockheader';
import Toggle from '@pelckmans/elementis/components/toggle';
import PinAltIcon from '@pelckmans/elementis/icons/pin-alt';
import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { buildGroupProps } from '../../../utils/elementisUtils';
import type { ModuleGroup } from '../utils';
import Method from './Method';

const findAnchorElementOutsideOfPinnedModules = (moduleId: string) =>
  // modules in pinned section are prefixed with "pinned-",
  // so this only finds modules outside of the pinned section.
  document.querySelector<HTMLElement>(`main [id="${moduleId}"]`);

export function MethodList({
  groupedModules,
  phasedOutModules,
  visitedModules,
  addedModuleIds,
  pinnedModules,
  onPinChange,
}: {
  groupedModules: ModuleGroup[];
  phasedOutModules: ModuleGroup[];
  visitedModules: string[];
  addedModuleIds: string[];
  pinnedModules: ModuleGroup[];
  onPinChange: (moduleId: string, nextPinned: boolean) => Promise<void>;
}) {
  const { t } = useTranslation();

  const [isPinModeActive, setPinModeActive] = useState(false);
  // pinnedModules already only contains pinned entries, so its ids double as the pinned set
  const pinnedModuleIdSet = new Set(pinnedModules.flatMap(group => group.modules.map(module => module.id)));

  const pinAnchor = useRef<{ moduleId: string; top: number } | null>(null);

  const handlePinChange = async (moduleId: string, nextPinned: boolean) => {
    const anchorElement = findAnchorElementOutsideOfPinnedModules(moduleId);
    pinAnchor.current = anchorElement ? { moduleId, top: anchorElement.getBoundingClientRect().top } : null;

    await onPinChange(moduleId, nextPinned);
  };

  const hasPinnedModules = pinnedModules.length > 0;
  const hadPinnedModules = useRef(hasPinnedModules);

  useLayoutEffect(() => {
    const pinnedSectionAppeared = hasPinnedModules && !hadPinnedModules.current;
    hadPinnedModules.current = hasPinnedModules;

    const anchor = pinAnchor.current;
    pinAnchor.current = null;

    // We scroll to the top rather than to the section itself since the content header overlays the pinned section.
    // If the pinned section is not the first section, this logic needs to be adjusted.
    if (pinnedSectionAppeared) {
      document.querySelector('main')?.scrollTo?.({ top: 0, behavior: 'smooth' });
      return;
    }

    if (!anchor) return;

    const anchorElement = findAnchorElementOutsideOfPinnedModules(anchor.moduleId);
    const scrollContainer = anchorElement?.closest('main');
    if (!anchorElement || !scrollContainer) return;

    scrollContainer.scrollTop += anchorElement.getBoundingClientRect().top - anchor.top;
  }, [pinnedModules, hasPinnedModules]);

  const pinToggle = (
    <Toggle
      label={t('PAGES.MY_METHODS.REGION.ACTIVE_MODULES.TOGGLE.LABEL')}
      checked={isPinModeActive}
      onChange={() => setPinModeActive(!isPinModeActive)}
    />
  );

  const pinTooltipProps = {
    children: t('PAGES.MY_METHODS.REGION.ACTIVE_MODULES.ALERT.PINNED.MESSAGE'),
  };

  return (
    <>
      {hasPinnedModules && (
        <section className="pinned-modules" aria-labelledby="pinned-modules-heading">
          <Blockheader
            id="pinned-modules-heading"
            title={t('PAGES.MY_METHODS.REGION.PINNED_MODULES.TITLE')}
            titleTag="h2"
            icon={<PinAltIcon data-testid="pinned-alt-icon" />}
            toggle={pinToggle}
            tooltipProps={pinTooltipProps}
          />
          <div className="modules-container">
            {pinnedModules.map(({ years, modules }) =>
              modules.map((module, index) => (
                <Method
                  key={module.id}
                  module={module}
                  position={index}
                  visitedModules={visitedModules}
                  years={years}
                  isPinModeActive={isPinModeActive}
                  isPinned={pinnedModuleIdSet.has(module.id)}
                  onPinChange={handlePinChange}
                  inPinnedSection
                />
              )),
            )}
          </div>
        </section>
      )}
      <section className="active-modules" aria-labelledby="active-modules-heading">
        <Blockheader
          id="active-modules-heading"
          title={t('PAGES.MY_METHODS.REGION.ACTIVE_MODULES.TITLE')}
          titleTag="h2"
          toggle={!hasPinnedModules ? pinToggle : undefined}
          tooltipProps={!hasPinnedModules ? pinTooltipProps : undefined}
          alertGroupProps={
            isPinModeActive
              ? buildGroupProps(
                  <Alert prominence="info">{t('PAGES.MY_METHODS.REGION.ACTIVE_MODULES.ALERT.PINNED.MESSAGE')}</Alert>,
                )
              : undefined
          }
        />
        <div className="modules-container">
          {groupedModules.map(({ years, modules }) =>
            modules.map((module, index: number) => {
              return (
                <Method
                  key={module.id}
                  module={module}
                  position={index}
                  visitedModules={visitedModules}
                  years={years}
                  isNew={addedModuleIds.includes(module.id)}
                  scrollTo={addedModuleIds[0] === module.id}
                  isPinModeActive={isPinModeActive}
                  isPinned={pinnedModuleIdSet.has(module.id)}
                  onPinChange={handlePinChange}
                />
              );
            }),
          )}
        </div>
      </section>
      {phasedOutModules.length > 0 && (
        <section className="phased-out-modules" aria-labelledby="phased-out-heading">
          <Blockheader
            id="phased-out-heading"
            title={t('PAGES.MY_METHODS.REGION.PHASED_OUT_MODULES.TITLE')}
            titleTag="h2"
            alertGroupProps={buildGroupProps(
              <>
                <Alert prominence="warning">
                  {t('PAGES.MY_METHODS.REGION.PHASED_OUT_MODULES.ALERT.PHASED_OUT.MESSAGE')}
                </Alert>
                {isPinModeActive ? (
                  <Alert prominence="info">
                    {t('PAGES.MY_METHODS.REGION.PHASED_OUT_MODULES.ALERT.PINNED.MESSAGE')}
                  </Alert>
                ) : undefined}
              </>,
            )}
          />
          <div className="modules-container">
            {phasedOutModules.map(({ years, modules }) =>
              modules.map((module, index) => (
                <Method
                  key={module.id}
                  module={module}
                  position={index}
                  visitedModules={visitedModules}
                  years={years}
                  isNew={addedModuleIds.includes(module.id)}
                  scrollTo={addedModuleIds[0] === module.id}
                  isPinModeActive={isPinModeActive}
                  isPinned={pinnedModuleIdSet.has(module.id)}
                  onPinChange={handlePinChange}
                />
              )),
            )}
          </div>
        </section>
      )}
    </>
  );
}
